import * as React from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import Table, { ColumnProps } from 'antd/lib/table'
import { Tooltip, Icon, Tag, Dropdown, Menu, Button } from 'antd'
import { ICustomer, API, PermissionNames } from '@getgreenline/homi-shared'
import moment from 'moment'
import { Currency } from '../../../../components/Currency'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import Highlighter from 'react-highlight-words'
import { splitHighlightWords } from '../../../../utilities/helpers'
import { CustomerTotalSpend } from '../../../../components/CustomerTotalSpend'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props {
  customers: ICustomer[]
  getCustomers: VoidFunction
  showAddCustomerModal: (selectedCustomer: ICustomer) => void
  highlightQuery: boolean
  searchQuery: string
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface CustomerListTableRecord {
  name: JSX.Element
  cardId: JSX.Element
  locationName: string | null
  email: JSX.Element
  phone: JSX.Element
  totalSpend: JSX.Element
  loyaltyBalance: JSX.Element
  dateAdded: string
  options: JSX.Element
}

export const CustomerTable = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer((props: Props & InjectedProps) => {
    const company = props.currentCompanyStore!.company!
    const canEditCustomers = props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_CUSTOMERS,
      company.id,
    )
    const canViewCustomerTotalSpend = !!props.currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      company.id,
    )
    const { customers, getCustomers, showAddCustomerModal } = props

    const baseColumn: ColumnProps<CustomerListTableRecord>[] = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: (
          <span>
            <span className='mr-2'>Card ID</span>
            <Tooltip
              title={
                <div>
                  <div>If your store generates customer IDs separately, enter them here.</div>
                  <div>Otherwise, {BLAZE_COMPANY} maintains its own internal ID system.</div>
                </div>
              }
            >
              <Icon type='question-circle' />
            </Tooltip>
          </span>
        ),
        dataIndex: 'cardId',
        key: 'cardId',
      },
      {
        title: 'Location',
        dataIndex: 'locationName',
        key: 'locationName',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
      },
      {
        title: 'Total spend',
        dataIndex: 'totalSpend',
        key: 'totalSpend',
        align: 'right',
      },
    ]

    const loyaltyColumn: ColumnProps<CustomerListTableRecord>[] = company.permissions
      .canAccessLoyalty
      ? [
          {
            title: 'Loyalty points',
            dataIndex: 'loyaltyPoints',
            key: 'loyaltyPoints',
            align: 'right',
          },
        ]
      : []

    const endColumn: ColumnProps<CustomerListTableRecord>[] = [
      {
        title: 'Date added',
        dataIndex: 'dateAdded',
        key: 'dateAdded',
      },
    ]
    const canAccessLoyalty =
      !props.currentCompanyStore?.canUseExternalLoyalty && !!company?.permissions?.canAccessLoyalty
    const columns: ColumnProps<CustomerListTableRecord>[] = canEditCustomers
      ? [
          ...baseColumn,
          ...(canAccessLoyalty ? loyaltyColumn : []),
          ...endColumn,
          {
            title: 'Options',
            dataIndex: 'options',
            key: 'options',
            align: 'center',
          },
        ]
      : baseColumn

    const tableDataSource: CustomerListTableRecord[] = customers.map((customer) => {
      return {
        key: customer.id,
        name: (
          <div
            className='cursor-pointer'
            onClick={() => showAddCustomerModal(customer)}
            style={{ color: 'var(--blue)' }}
          >
            <Highlighter
              highlightClassName='highlighted-text'
              searchWords={props.highlightQuery ? splitHighlightWords(props.searchQuery) : []}
              textToHighlight={customer.name}
              autoEscape
            />
          </div>
        ),
        cardId: (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={props.highlightQuery ? splitHighlightWords(props.searchQuery) : []}
            textToHighlight={customer.cardId || ''}
            autoEscape
          />
        ),
        locationName: customer.locationName,
        email: (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={props.highlightQuery ? splitHighlightWords(props.searchQuery) : []}
            textToHighlight={customer.email || ''}
            autoEscape
          />
        ),
        phone: (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={props.highlightQuery ? splitHighlightWords(props.searchQuery) : []}
            textToHighlight={customer.phone || ''}
            autoEscape
          />
        ),
        totalSpend: (
          <CustomerTotalSpend
            canViewCustomerTotalSpend={canViewCustomerTotalSpend}
            totalSpend={customer.totalSpend}
          />
        ),
        loyaltyBalance: (
          <>
            <Tag className='mr-0 mb-1'>{customer.loyaltyTierName || 'No loyalty'}</Tag>
            <div>{<Currency centValue={customer.creditBalance} />}</div>
          </>
        ),
        loyaltyPoints: (
          <>
            <Tag className='mr-0 mb-1'>{customer.loyaltyTierName || 'No loyalty'}</Tag>
            <div>{customer.creditBalance} points</div>
          </>
        ),
        dateAdded: moment(customer.createDate).format('MMM DD YYYY'),
        options: (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item key='edit' onClick={() => showAddCustomerModal(customer)}>
                  <span style={{ color: 'var(--blue)' }}>Edit</span>
                </Menu.Item>

                <Menu.Item
                  key='delete'
                  onClick={async () => {
                    if (window.confirm('Are you sure?')) {
                      await API.deleteCustomer(company.id, customer.id)
                      await getCustomers()
                    }
                  }}
                >
                  <span style={{ color: 'var(--red)' }}>Delete</span>
                </Menu.Item>
              </Menu>
            }
          >
            <Button icon='ellipsis' style={{ transform: 'rotate(90deg)' }} />
          </Dropdown>
        ),
      }
    })

    return (
      <Table
        dataSource={tableDataSource}
        className='no-background-table intercom-pb-3'
        rowClassName={() => 'background-white'}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    )
  }),
)
